/**
 * «Путь пешки» — the 10-stage curriculum (docs/research/05 §3 + §7, with the corrections of its
 * «Верификация» section applied):
 *  - puzzleThemes contain ONLY real tags of the Lichess puzzle CSV. `mix` (not a tag), `oneMove`
 *    (= mateIn1), `castling` (rare and hard) and `opening` (a phase tag) are never used.
 *  - Stage 1 has almost no ready-made Lichess content ("take the free piece" one-movers do not
 *    exist in the database) — its drills are two-move `hangingPiece` puzzles plus easy `mateIn1`.
 *  - Numeric thresholds are project values to be calibrated on the child's real data; puzzle
 *    ratings are on the Lichess puzzle scale (noticeably higher than a playing rating).
 *  - A stage is never taken away: on regress the app adds repetition instead.
 */
import type { CurriculumStage } from '@gambit/shared';

export const CURRICULUM: CurriculumStage[] = [
  {
    stage: 1,
    title: 'Пешка на старте: зоркий глаз',
    ratingBand: '0–600',
    goal: 'Видеть, кто на кого нападает, забирать бесплатное и не оставлять свои фигуры без защиты.',
    skills: [
      'Знаю, сколько стоит каждая фигура',
      'Вижу, кто на кого нападает и кто кого защищает',
      'Забираю то, что отдают бесплатно',
      'Увожу или защищаю свою фигуру, когда на неё напали',
      'Считаю размен: кто кого дороже',
      'Ухожу от шаха тремя способами: уйти, закрыться, съесть',
      'Быстро нахожу любую клетку по её имени',
      'Перед ходом задаю два вопроса: «Что он хочет?» и «Мой ход безопасен?»',
    ],
    puzzleThemes: ['hangingPiece', 'mateIn1'],
    endgames: [],
    openingFocus: 'Пока без дебютов: пешкой в центр и выводим фигуры. Главное — ничего не отдавать даром.',
    mastery: {
      description:
        'Задачи на незащищённую фигуру решаются на рейтинге 900 и выше. В партиях с Петей — не больше трёх простых зевков за игру, а бесплатные фигуры забираются в четырёх случаях из пяти.',
      minPuzzleRating: 900,
      maxBlundersPerGame: 3,
    },
    recommendedPersonas: ['petya', 'sonya'],
  },
  {
    stage: 2,
    title: 'Первый шаг: мат и три золотых правила',
    ratingBand: '600–800',
    goal: 'Ставить мат в один ход и простые маты тяжёлыми фигурами, начинать партию по трём золотым правилам.',
    skills: [
      'Мат в один ход: узнаю знакомые картинки',
      'Мат двумя ладьями «лесенкой»',
      'Мат ферзём и королём «коробочкой»',
      'Мат ладьёй и королём',
      'Знаю, что такое пат, и не допускаю его',
      'Знаю, какими фигурами мат поставить нельзя',
      'Три золотых правила дебюта: центр, фигуры, рокировка',
      'Детский мат: умею защищаться от него',
    ],
    puzzleThemes: ['mateIn1', 'backRankMate', 'hangingPiece'],
    endgames: [
      'Мат двумя ладьями («лесенка»)',
      'Мат ферзём и королём («коробочка»), без пата',
      'Мат ладьёй и королём',
      'Пат: как узнать и как не допустить; чем нельзя поставить мат',
    ],
    openingFocus:
      'Три золотых правила: пешкой в центр, разбуди коней и слонов, спрячь короля рокировкой. Ферзя рано не выводим.',
    mastery: {
      description:
        'Мат в один ход решается на рейтинге 1100 и выше. «Коробочка» и «лесенка» получаются пять раз из пяти и без пата, мат ладьёй — четыре раза из пяти. В восьми партиях из десяти дебют сыгран по трём правилам, простых зевков — не больше двух за игру.',
      minPuzzleRating: 1100,
      maxBlundersPerGame: 2,
    },
    recommendedPersonas: ['sonya', 'grisha'],
  },
  {
    stage: 3,
    title: 'Пешка идёт в бой: двойной удар',
    ratingBand: '800–1000',
    goal: 'Находить вилки, связки и сквозные удары, считать нападающих и защитников, ставить мат в два хода.',
    skills: [
      'Вилка конём, ферзём и пешкой',
      'Связка: приклеенная фигура',
      'Сквозной удар',
      'Считаю нападающих и защитников',
      'Мат в два хода',
      'Вижу угрозу мата в один ход и защищаюсь',
      'Три вопроса перед ходом и «ШВУ»: шахи, взятия, угрозы',
    ],
    puzzleThemes: ['fork', 'pin', 'skewer', 'mateIn2', 'short'],
    endgames: ['Правило квадрата', 'Король и пешка против короля: пешка на шестой линии, первые шаги оппозиции'],
    openingFocus: 'Те же три золотых правила. Плюс: не ходи одной фигурой дважды и соедини ладьи.',
    mastery: {
      description:
        'Вилка, связка и сквозной удар решаются на рейтинге 1200 и выше, мат в два хода — от 1100. На угрозы соперника в один ход есть ответ в семи случаях из десяти. Простых зевков — в среднем не больше полутора за игру, а слишком быстрых ходов — меньше трети.',
      minPuzzleRating: 1200,
      maxBlundersPerGame: 1.5,
    },
    recommendedPersonas: ['grisha', 'sasha'],
  },
  {
    stage: 4,
    title: 'Середина доски: убери защитника',
    ratingBand: '1000–1200',
    goal: 'Освоить вскрытые нападения, двойной шах, удары по защитнику и научиться защищаться от тактики.',
    skills: [
      'Вскрытое нападение и вскрытый шах',
      'Двойной шах',
      'Уничтожение защитника',
      'Отвлечение и завлечение',
      'Слабость последней линии',
      'Четыре способа защиты: уйти, защитить, перекрыть, ответить ударом',
      'Дебютные ловушки: как в них не попасться',
    ],
    puzzleThemes: [
      'discoveredAttack',
      'discoveredCheck',
      'doubleCheck',
      'capturingDefender',
      'deflection',
      'attraction',
      'backRankMate',
      'defensiveMove',
    ],
    endgames: [
      'Король и пешка против короля: оппозиция',
      'Ключевые поля',
      'Ладейная пешка — ничья; как запирают короля',
      'Слон и ладейная пешка «не того цвета» — ничья',
    ],
    openingFocus:
      'Знакомые ловушки: детский мат, мат Легаля, «жареная печень». Учимся не попадаться и наказывать ранний выход ферзя.',
    mastery: {
      description:
        'Темы ступени решаются на рейтинге 1400 и выше, задачи вперемешку — от 1300. Король с пешкой против короля: и выигрыш, и ничья получаются четыре раза из пяти. Простых зевков — не больше одного за игру, точность партий — 65 и выше.',
      minPuzzleRating: 1400,
      maxBlundersPerGame: 1,
      minAccuracy: 65,
    },
    recommendedPersonas: ['sasha', 'vika'],
  },
  {
    stage: 5,
    title: 'Переход границы: считаю на три хода',
    ratingBand: '1200–1400',
    goal: 'Считать форсированные варианты в уме на три хода вперёд и рассказывать о своих партиях.',
    skills: [
      'Считаю варианты в уме, не двигая фигуры',
      'Промежуточный ход',
      'Ловля фигуры',
      '«Рентген»: фигура смотрит сквозь другую',
      'Игра вслепую понемногу: цвет клетки, путь коня',
      'Заканчиваю развитие и выбираю план после дебюта',
      'Рассказываю вслух, что было важного в моей партии',
    ],
    puzzleThemes: ['intermezzo', 'trappedPiece', 'xRayAttack', 'mateIn3', 'pawnEndgame', 'promotion', 'advancedPawn', 'long'],
    endgames: [
      'Защищённая и отдалённая проходная; пешечный прорыв',
      'Треугольник, далёкая оппозиция, «отталкивание плечом»',
      'Ферзь против пешки на седьмой линии',
    ],
    openingFocus:
      'Первый репертуар по идеям: Итальянская партия за белых; за чёрных — 1...e5 в ответ на 1.e4 и 1...d5 в ответ на 1.d4.',
    mastery: {
      description:
        'Задачи вперемешку решаются на рейтинге 1600 и выше, мат в три хода — от 1400. Упражнения на игру вслепую — восемь верных из десяти. Зевков — не больше двух-трёх за игру, точность — 72 и выше. Наговорено не меньше десяти разборов своих партий, из них четыре — проигранные.',
      minPuzzleRating: 1600,
      maxBlundersPerGame: 2.5,
      minAccuracy: 72,
    },
    recommendedPersonas: ['vika', 'lyova'],
  },
  {
    stage: 6,
    title: 'Проходная пешка: атака и план',
    ratingBand: '1400–1600',
    goal: 'Готовить комбинации, атаковать короля и доводить лишний материал до победы.',
    skills: [
      'Подготовительные ходы в комбинации: завлечение, освобождение, перекрытие, блокировка',
      'Атака на короля: слабые пункты и типовые жертвы',
      'Открытые линии и седьмая линия для ладей',
      'Слабые пешки соперника',
      'Реализация перевеса: меняю фигуры, а не пешки',
      'Улучшаю свою самую плохую фигуру',
    ],
    puzzleThemes: [
      'kingsideAttack',
      'attackingF2F7',
      'sacrifice',
      'clearance',
      'interference',
      'exposedKing',
      'rookEndgame',
      'quietMove',
    ],
    endgames: [
      'Ладья против пешки: счёт темпов, отрезание короля',
      'Позиция Лусены («мост»)',
      'Позиция Филидора (оборона по шестой линии)',
      'Ладья позади проходной; активная ладья против пассивной',
      'Лёгкая фигура против пешки; конь против ладейной пешки',
    ],
    openingFocus:
      'Итальянская партия глубже: план с сильным центром и тихий план. По желанию — гамбит Эванса как урок «время важнее пешки».',
    mastery: {
      description:
        'Задачи вперемешку решаются на рейтинге 1800 и выше. «Мост» Лусены и оборона Филидора получаются четыре раза из пяти. Позиции с лишней фигурой выигрываются в восьми случаях из десяти. Точность — 76 и выше, зевков — в среднем не больше полутора за игру.',
      minPuzzleRating: 1800,
      maxBlundersPerGame: 1.5,
      minAccuracy: 76,
    },
    recommendedPersonas: ['lyova', 'nika'],
  },
  {
    stage: 7,
    title: 'В шаге от превращения: позиционная игра',
    ratingBand: '1600–1800',
    goal: 'Понимать пешечные структуры, сильные и слабые поля и строить план по позиции.',
    skills: [
      'Пешечные структуры: изолированная пешка, висячие пешки, цепи, большинство',
      'Сильные и слабые поля, форпост',
      'Хороший и плохой слон; слон против коня',
      'План по пешечной структуре',
      'Защита: активная и экономная',
      'Профилактика: «Что сделает соперник, если ход его?»',
      'Разыгрываю типовые позиции против движка',
      'Первый «дятел»: набор задач в несколько кругов',
    ],
    puzzleThemes: [
      'zugzwang',
      'defensiveMove',
      'quietMove',
      'bishopEndgame',
      'knightEndgame',
      'queenEndgame',
      'veryLong',
      'middlegame',
    ],
    endgames: [
      'Ладья на седьмой линии',
      'Короткая и длинная сторона в ладейных окончаниях',
      'Разноцветные слоны: ничейные тенденции и крепости',
      'Слон против коня; хороший и плохой слон в эндшпиле',
      'Пешечные гонки и превращение с шахом',
    ],
    openingFocus: 'Репертуар тот же, но теперь по структурам: какой план у каждой стороны после дебюта.',
    mastery: {
      description:
        'Задачи вперемешку решаются на рейтинге 2000 и выше. Тесты по темам — не ниже 60 из 100 по каждой теме и 75 в среднем. Точность — 80 и выше, зевков — не больше одного за игру, с Лёвой и Никой — не меньше половины очков.',
      minPuzzleRating: 2000,
      maxBlundersPerGame: 1,
      minAccuracy: 80,
    },
    recommendedPersonas: ['lyova', 'nika'],
  },
  {
    stage: 8,
    title: 'Превращение: техника и защита',
    ratingBand: '1800–2000',
    goal: 'Реализовывать перевес, упорно защищать трудные позиции и дисциплинированно считать варианты.',
    skills: [
      'Реализация перевеса и принцип двух слабостей',
      'Размены как стратегическое оружие',
      'Упорная защита худших позиций',
      'Дисциплина счёта: все кандидаты за обе стороны',
      'Распределение времени: где думать долго, а где быстро',
      'Репертуар по структурам с конкретными вариантами',
      'Письменный журнал анализа своих партий',
    ],
    puzzleThemes: ['endgame', 'queenRookEndgame', 'rookEndgame', 'defensiveMove', 'equality', 'mateIn4'],
    endgames: [
      'Защита Ванчуры',
      'Ладейные окончания: четыре пешки против трёх на одном фланге',
      'Слон и пешка против слона (одноцветные)',
      'Разноцветные слоны с двумя лишними пешками',
      'Мат двумя слонами; мат слоном и конём',
      'Принцип двух слабостей; реализация лишней пешки',
    ],
    openingFocus:
      'Расширяем репертуар: Испанская партия за белых, ферзевый гамбит или Лондонская система как второй дебют.',
    mastery: {
      description:
        'Задачи вперемешку решаются на рейтинге 2200 и выше. Эндшпильные упражнения ступени получаются четыре раза из пяти. Точность — 84 и выше, с Никой — не меньше половины очков. Две глубоко разобранные партии в месяц записаны в журнал.',
      minPuzzleRating: 2200,
      maxBlundersPerGame: 0.5,
      minAccuracy: 84,
    },
    recommendedPersonas: ['nika', 'dima'],
  },
  {
    stage: 9,
    title: 'Новая фигура: кандидат в мастера',
    ratingBand: '2000–2200',
    goal: 'Глубоко анализировать свои партии без движка, решать сложные позиции на счёт и играть в турнирах.',
    skills: [
      'Сначала анализирую партию без движка, потом сверяюсь с ним',
      'Показываю анализы тренеру-человеку',
      'Жертва качества, динамика против статики',
      'Этюды и позиции на счёт по 15–20 минут',
      'Турнирный режим и психология',
    ],
    puzzleThemes: ['mateIn5', 'sacrifice', 'quietMove', 'zugzwang', 'veryLong', 'master'],
    endgames: [
      'Ферзевые окончания: ферзь и пешка против ферзя, вечный шах',
      'Ладья и слон против ладьи: защита',
      'Сложные ладейные окончания',
    ],
    openingFocus: 'Свой турнирный репертуар с конкретными вариантами и модельными партиями.',
    mastery: {
      description:
        'Задачи вперемешку решаются на рейтинге 2400 и выше, точность — 87 и выше. Главный критерий здесь уже внешний: официальный рейтинг и результаты в турнирах.',
      minPuzzleRating: 2400,
      maxBlundersPerGame: 0.5,
      minAccuracy: 87,
    },
    recommendedPersonas: ['nika', 'dima'],
  },
  {
    stage: 10,
    title: 'Ферзь: путь мастера',
    ratingBand: '2200+',
    goal: 'Индивидуальный план с сильным живым тренером; приложение — спарринг-партнёр и тетрадь ошибок.',
    skills: [
      'Спарринг с движком в заданных позициях',
      'База своих партий и своих ошибок',
      'Проверка домашних анализов',
      'Поддержание тактической формы кругами задач',
      'Дебютная картотека с повторениями',
    ],
    puzzleThemes: ['masterVsMaster', 'superGM', 'veryLong', 'zugzwang'],
    endgames: ['Выборочно по учебнику эндшпиля и по своим партиям'],
    openingFocus: 'Личная дебютная картотека, которую ведёт сам шахматист вместе с тренером.',
    mastery: {
      description:
        'Здесь нет финиша: цели ставит живой тренер. Приложение помогает держать форму и хранит историю роста.',
    },
    recommendedPersonas: ['dima'],
  },
];

/** Stage lookup clamped to the valid range 1..10. */
export function getCurriculumStage(stage: number): CurriculumStage {
  const last = CURRICULUM.length;
  const n = Number.isFinite(stage) ? Math.min(last, Math.max(1, Math.trunc(stage))) : 1;
  // CURRICULUM is ordered by stage and has no gaps (covered by tests).
  return CURRICULUM[n - 1] as CurriculumStage;
}

/** The stage after `stage`, or null on the last one. */
export function getNextCurriculumStage(stage: number): CurriculumStage | null {
  const current = getCurriculumStage(stage);
  return current.stage >= CURRICULUM.length ? null : getCurriculumStage(current.stage + 1);
}
